import React from 'react';
import { Row, Col } from 'react-bootstrap';
import { MONTHS, formatACFT, getDiversionSign, isWholesaleType } from '../../dwriUtils';

const SourceTable = ({ source }) => (
    <div>
        <Row className="mt-3 gx-0">
            <Col xs="auto" className="pe-4">
                <span><b>Status: </b>{source.sourceStatus}</span>
            </Col>
            <Col xs="auto" className="pe-4">
                <span><b>Name: </b>{source.sourceName}</span>
            </Col>
            <Col xs="auto" className="pe-4">
                <span><b>Type: </b>{source.sourceType}</span>
            </Col>
            <Col xs="auto">
                <span><b>Diversion Type: </b>{source.diversionType}</span>
            </Col>
        </Row>
        <div className="table-responsive">
            <Row className='dwri-source-monthly-header gx-0'>
                <Col className='dwri-table-header-cell'><span><b>Year</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Jan</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Feb</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Mar</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Apr</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>May</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Jun</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Jul</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Aug</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Sep</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Oct</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Nov</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>Dec</b></span></Col>
                <Col className='dwri-table-header-cell'><span><b>ACFT</b></span></Col>
            </Row>
            {[...source.records]
                .sort((a, b) => b.recordYear - a.recordYear)
                .map((record, rIdx) => (
                    <Row key={rIdx} className={`dwri-source-monthly-row gx-0 ${rIdx % 2 === 0 ? 'is-even' : 'is-odd'}`}>
                        <Col className='dwri-table-cell'><span>{record.recordYear}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.jan)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.feb)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.mar)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.apr)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.may)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.jun)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.jul)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.aug)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.sep)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.oct)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.nov)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.dec)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(record.total)}</span></Col>
                    </Row>
                ))
            }
        </div>
    </div>
);

const DwriSourceDataSection = ({ sourceRecords, selectedSources, loading, error }) => {
    if (error) return <p className="text-danger">Error loading source data.</p>;
    if (loading && sourceRecords.length === 0) return <p>Loading...</p>;

    const filtered = selectedSources.length > 0
        ? sourceRecords.filter(r => selectedSources.includes(String(r.sourceId)))
        : sourceRecords;
    // Fall back to all records if the filter produces no matches (e.g. ID scheme mismatch between resolvers)
    const filteredRecords = filtered.length > 0 ? filtered : sourceRecords;

    // Build yearly aggregate totals for the Source Monthly Data summary table
    const yearlyTotalsMap = {};
    filteredRecords.forEach(record => {
        const sign = getDiversionSign(record.diversionType);
        (record.YearRecords || []).forEach(yearRecord => {
            const year = yearRecord.recordYear;
            if (!yearlyTotalsMap[year]) {
                yearlyTotalsMap[year] = { jan: 0, feb: 0, mar: 0, apr: 0, may: 0, jun: 0, jul: 0, aug: 0, sep: 0, oct: 0, nov: 0, dec: 0 };
            }
            MONTHS.forEach(month => {
                yearlyTotalsMap[year][month] += (yearRecord[month] || 0) * sign;
            });
        });
    });

    const sourceMonthlyRows = Object.entries(yearlyTotalsMap)
        .map(([year, monthData]) => ({
            year: parseInt(year),
            ...monthData,
            feet: MONTHS.reduce((sum, m) => sum + monthData[m], 0),
        }))
        .sort((a, b) => b.year - a.year);

    // Group records by sourceId for the per-source tables
    const sourceGroups = {};
    filteredRecords.forEach(record => {
        sourceGroups[record.sourceId] = {
            sourceId: record.sourceId,
            sourceName: record.sourceName,
            sourceStatus: record.sourceStatus,
            sourceType: record.sourceType,
            diversionType: record.diversionType,
            records: record.YearRecords || [],
        };
    });

    const allSourceGroups = Object.values(sourceGroups);
    const sourceSections = allSourceGroups
        .filter(s => !isWholesaleType(s.diversionType))
        .sort((a, b) => (a.diversionType || '').localeCompare(b.diversionType || ''));
    const wholesaleSections = allSourceGroups
        .filter(s => isWholesaleType(s.diversionType))
        .sort((a, b) => (a.diversionType || '').localeCompare(b.diversionType || ''));

    return (
        <>
            <Row>
                <Col><span><b>Source Monthly Data (ACFT)</b></span></Col>
            </Row>
            <div className="table-responsive">
                <Row className='dwri-source-monthly-header gx-0'>
                    <Col className='dwri-table-header-cell'><span><b>Year</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Jan</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Feb</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Mar</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Apr</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>May</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Jun</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Jul</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Aug</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Sep</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Oct</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Nov</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Dec</b></span></Col>
                    <Col className='dwri-table-header-cell'><span><b>Feet</b></span></Col>
                </Row>
                {sourceMonthlyRows.map((row, index) => (
                    <Row key={index} className={`dwri-source-monthly-row gx-0 ${index % 2 === 0 ? 'is-even' : 'is-odd'}`}>
                        <Col className='dwri-table-cell'><span>{row.year}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.jan)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.feb)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.mar)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.apr)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.may)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.jun)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.jul)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.aug)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.sep)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.oct)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.nov)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.dec)}</span></Col>
                        <Col className='dwri-table-cell'><span>{formatACFT(row.feet)}</span></Col>
                    </Row>
                ))}
            </div>
            <br />

            <Row>
                <Col><span><b>Sources</b></span></Col>
            </Row>
            {sourceSections.length > 0
                ? sourceSections.map((source, idx) => (
                    <SourceTable key={idx} source={source} />
                ))
                : <p className="text-muted ms-2 mt-2">No source data available.</p>
            }
            <br />

            <Row>
                <Col><span><b>Wholesale</b></span></Col>
            </Row>
            {wholesaleSections.length > 0
                ? wholesaleSections.map((source, idx) => (
                    <SourceTable key={idx} source={source} />
                ))
                : <p className="text-muted ms-2 mt-2">No wholesale data available.</p>
            }
        </>
    );
};

export default DwriSourceDataSection;
